import type * as THREE from 'three/webgpu';

/** A layer no camera looks at: objects wait here until their turn. */
const WAITING_LAYER = 31;

/**
 * Brings a world's shaders in a few at a time. three.js builds an object's shader code (CPU
 * work) and its pipeline the first time it is drawn. Pipelines compile in the background
 * (AsyncPipelines), but building every material's code in one frame would still stall it.
 * Hiding every object and revealing one material per frame spreads that over many frames,
 * and between them the loading screen can show progress. On later visits the browser has
 * the compiled shaders cached.
 *
 * Call `step()` once per frame before rendering until `done`.
 */
export class ShaderWarmup {
  private readonly groups: THREE.Object3D[][];
  private readonly masks = new Map<THREE.Object3D, number>();
  private next = 0;

  constructor(scenes: THREE.Object3D[]) {
    const byMaterial = new Map<string, THREE.Object3D[]>();
    for (const scene of scenes) {
      scene.traverse((o) => {
        const m = (o as THREE.Mesh).material;
        if (!m) return;
        const key = Array.isArray(m) ? m.map((x) => x.uuid).join() : m.uuid;
        let group = byMaterial.get(key);
        if (!group) {
          group = [];
          byMaterial.set(key, group);
        }
        group.push(o);
        this.masks.set(o, o.layers.mask);
        o.layers.set(WAITING_LAYER);
      });
    }
    this.groups = [...byMaterial.values()];
  }

  /** Frames needed: one with only post-processing, then one per material. */
  get total(): number {
    return this.groups.length + 1;
  }

  get done(): boolean {
    return this.next > this.groups.length;
  }

  /** 0–1: how many of the frames have been drawn. */
  get progress(): number {
    return Math.min(1, this.next / this.total);
  }

  /** Reveal the next material's objects (the first call reveals nothing). */
  step(): void {
    if (this.done) return;
    if (this.next > 0) {
      for (const o of this.groups[this.next - 1]) o.layers.mask = this.masks.get(o) ?? 1;
    }
    this.next++;
  }
}
